import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { AgentLandingData, PromptSuggestion } from '../../models';
import { LoggerService } from '../../../../services/logger-services/logger.service';
import { AgentConfigService } from '../../services/agent-config/agent-config.service';

/**
 * Agent Landing Page Component
 * 
 * Displays welcome/landing page for agents with:
 * - Agent name and icon
 * - Personalized introduction text
 * - Helpful tips
 * - Suggested prompts to get started
 * 
 * Features:
 * - Loads agent configuration from JSON file
 * - Personalizes content with user's first name
 * - Sanitizes HTML content for security
 * - Gracefully degrades if config unavailable
 * - Emits selected prompt to parent for processing
 * 
 * Usage:
 * <app-agent-landing
 *   [agentId]="currentAgentId"
 *   [userFName]="userName"
 *   (promptSelected)="onPromptClick($event)">
 * </app-agent-landing>
 */
@Component({
  selector: 'app-agent-landing',
  templateUrl: './agent-landing.component.html',
  styleUrls: ['./agent-landing.component.scss']
})
export class AgentLandingComponent implements OnInit, OnChanges {
  @Input() agentId: string = '';
  @Input() userFName: string = 'User';
  
  @Output() promptSelected = new EventEmitter<string>();
  
  agentConfig: AgentLandingData | null = null;
  personalizedIntro: string = '';
  isLoading: boolean = true;

  constructor(
    private readonly logger: LoggerService,
    private readonly agentConfigService: AgentConfigService
  ) {}

  /**
   * Initialize component and load agent configuration
   */
  ngOnInit(): void {
    // Only load if agentId is provided
    if (this.agentId && this.agentId !== 'module') {
      this.loadAgentConfig().catch((error: any) => {
        this.logger.logInfo('[AgentLanding] Error in ngOnInit', 'ngOnInit', error);
      });
    } else {
      console.log('[AgentLanding] Skipping load - waiting for valid agentId');
      this.isLoading = false;
    }
  }

  /**
   * Reload configuration when agent ID changes
   * 
   * @param changes - Component input changes
   */
  ngOnChanges(changes: SimpleChanges): void {
    // Handle agent ID changes (including first time it's set)
    if (changes['agentId']) {
      const agentIdChange = changes['agentId'];
      if (agentIdChange.firstChange) {
        return; // Skip reload on first binding; ngOnInit already loads
      }
      const newAgentId = agentIdChange.currentValue;
      console.log('[AgentLanding] Agent ID changed to:', newAgentId);
      
      // Only load if we have a valid agent ID (not empty, not 'module')
      if (newAgentId && newAgentId !== 'module') {
        this.isLoading = true;
        this.loadAgentConfig().catch((error: any) => {
          this.logger.logInfo('[AgentLanding] Error in ngOnChanges', 'ngOnChanges', error);
        });
      } else {
        console.log('[AgentLanding] Skipping load - invalid agentId:', newAgentId);
        this.isLoading = false;
      }
    }
  }

  /**
   * Load agent configuration from JSON file
   * Gracefully handles missing file or configuration
   * 
   * @returns Promise<void>
   */
  private async loadAgentConfig(): Promise<void> {
    try {
      // Validate agentId before attempting to load
      if (!this.agentId || this.agentId === 'module') {
        console.warn('[AgentLanding] Invalid agentId, skipping config load:', this.agentId);
        this.agentConfig = null;
        this.isLoading = false;
        return;
      }
      
      console.log('[AgentLanding] Loading config for agent:', this.agentId);
      
      // Use centralized config service (already loaded at app startup)
      const agent = this.agentConfigService.getAgentConfig(this.agentId);
      
      console.log('[AgentLanding] Retrieved agent config:', agent ? 'Found' : 'Not found');
      
      if (agent) {
        this.agentConfig = {
          id: agent.id,
          display_name: agent.display_name,
          icon: agent.icon,
          landing_page: agent.landing_page
        };
        
        console.log('[AgentLanding] Agent config loaded successfully:', this.agentConfig.display_name);
        console.log('[AgentLanding] Landing page data:', {
          title: this.agentConfig.landing_page?.title,
          hasIntro: !!(this.agentConfig.landing_page?.intro ?? this.agentConfig.landing_page?.short_intro),
          promptCount: agent.prompt_library?.length || 0
        });
          
        // Single-agent landing page: show full intro (particular agent page); fallback to short_intro if intro missing
        const introText = this.agentConfig.landing_page?.intro ?? this.agentConfig.landing_page?.short_intro ?? '';
        if (introText) {
          this.personalizedIntro = introText.replace(/\{\{userFName\}\}/, this.userFName);
        }
      } else {
        console.warn('[AgentLanding] No config found for agent:', this.agentId);
        this.agentConfig = null;
      }
    } catch (error: any) {
      console.error('[AgentLanding] Error loading agent config:', error);
      this.logger.logInfo('[AgentLanding] Error loading agent config', 'loadAgentConfig', error);
      this.agentConfig = null;
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Handle prompt suggestion click
   * Emits selected prompt text to parent
   * 
   * @param prompt - Selected prompt suggestion
   */
  onPromptClick(prompt: PromptSuggestion): void {
    try {
      this.promptSelected.emit(prompt.text);
    } catch (error: any) {
      this.logger.logInfo('[AgentLanding] Error handling prompt click', 'onPromptClick', error);
    }
  }

  /**
   * Get array of prompt suggestions
   * 
   * @returns PromptSuggestion[] - Array of suggestions or empty array
   */
  get promptSuggestions(): PromptSuggestion[] {
    try {
      return this.agentConfig?.landing_page?.prompt_suggestions || [];
    } catch (error: any) {
      this.logger.logInfo('[AgentLanding] Error getting prompt suggestions', 'promptSuggestions', error);
      return [];
    }
  }

  /**
   * Get array of helpful tips
   * 
   * @returns string[] - Array of tips or empty array
   */
  get tips(): string[] {
    try {
      return this.agentConfig?.landing_page?.tips || [];
    } catch (error: any) {
      this.logger.logInfo('[AgentLanding] Error getting tips', 'tips', error);
      return [];
    }
  }

  /**
   * Check if agent configuration loaded successfully
   * 
   * @returns boolean - True if config exists
   */
  get hasConfig(): boolean {
    try {
      return this.agentConfig !== null;
    } catch (error: any) {
      this.logger.logInfo('[AgentLanding] Error checking config status', 'hasConfig', error);
      return false;
    }
  }

  /**
   * Get agent title for display
   * 
   * @returns string - Agent title or empty string
   */
  get agentTitle(): string {
    try {
      return this.agentConfig?.landing_page?.title || this.agentConfig?.display_name || '';
    } catch (error: any) {
      this.logger.logInfo('[AgentLanding] Error getting agent title', 'agentTitle', error);
      return '';
    }
  }

  /**
   * Get agent icon path
   * 
   * @returns string | undefined - Icon path or undefined
   */
  get agentIcon(): string | undefined {
    try {
      return this.agentConfig?.icon;
    } catch (error: any) {
      this.logger.logInfo('[AgentLanding] Error getting agent icon', 'agentIcon', error);
      return undefined;
    }
  }
}
